/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE } from "utils";

function RekapLayanan() {
  const [rekapList, setRekapList] = useState([]);
  const [selectedTahun, setSelectedTahun] = useState("");
  const [tahunList, setTahunList] = useState([]);
  const [dinasId, setDinasId] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
    } else {
      logoutUser(navigate);
    }
  }, []);

  // Generate tahun list dari current year sampai 2025
  const generateTahunList = () => {
    const currentYear = new Date().getFullYear();
    const tahunArray = [];

    // Generate dari current year sampai 2025
    for (let tahun = currentYear; tahun >= 2025; tahun--) {
      tahunArray.push(tahun);
    }

    setTahunList(tahunArray);

    // Set default tahun ke current year
    if (tahunArray.length > 0) {
      setSelectedTahun(currentYear.toString());
    }
  };

  useEffect(() => {
    generateTahunList();
  }, []);

  // Fetch rekap data
  const fetchRekap = async (tahun) => {
    if (!tahun) {
      setRekapList([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}survey-kepuasan/layanan-publik/rekap?tahun=${tahun}`
      );
      setRekapList(response.data || []);
    } catch (error) {
      console.error("Error fetching rekap:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data rekap layanan",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle tahun change
  const handleTahunChange = (e) => {
    const tahun = e.target.value;
    setSelectedTahun(tahun);
    if (tahun) {
      fetchRekap(tahun);
    }
  };

  // Auto fetch rekap ketika tahun sudah dipilih
  useEffect(() => {
    if (selectedTahun) {
      fetchRekap(selectedTahun);
    }
  }, [selectedTahun]);

  // Define columns for DataTable
  const columns = [
    { Header: "No", accessor: "no", width: "5%" },
    { Header: "Layanan", accessor: "layanan", width: "25%" },
    {
      Header: "Dinas",
      accessor: "nama_dinas",
      width: 200,
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ value }) => (
        <MDTypography
          variant="body2"
          sx={{
            wordWrap: "break-word",
            whiteSpace: "normal",
            lineHeight: 1.4,
            width: "200px",
            maxWidth: "200px",
            minWidth: "200px",
            display: "block",
          }}
        >
          {value || "-"}
        </MDTypography>
      ),
    },
    {
      Header: "Total Responden",
      accessor: "total_responden",
      width: "15%",
      Cell: ({ value }) => (
        <MDTypography variant="body2" fontWeight="medium">
          {value || 0}
        </MDTypography>
      ),
    },
    {
      Header: "Rata-rata Nilai",
      accessor: "rata_rata_nilai",
      width: "15%",
      Cell: ({ value }) => (
        <MDTypography variant="body2" fontWeight="medium" color="success">
          {value > 0 ? value.toFixed(2) : "0.00"}
        </MDTypography>
      ),
    },
    {
      Header: "Rata-rata Skor",
      accessor: "rata_rata_skor",
      width: "20%",
      Cell: ({ value, row }) => (
        <MDBox>
          <MDTypography
            variant="body2"
            fontWeight="medium"
            color="info"
            mb={0.5}
          >
            {value > 0 ? value.toFixed(2) : "0.00"}
          </MDTypography>
          <MDProgress
            value={value > 0 ? value : 0}
            color="info"
            variant="gradient"
            label={false}
          />
        </MDBox>
      ),
    },
  ];

  // Calculate aggregated statistics
  const totalLayanan = rekapList.length;
  const totalResponden = rekapList.reduce(
    (sum, rekap) => sum + (rekap.total_responden || 0),
    0
  );
  const rataRataNilaiAll =
    rekapList.length > 0
      ? rekapList.reduce(
          (sum, rekap) => sum + (rekap.rata_rata_nilai || 0),
          0
        ) / rekapList.length
      : 0;
  const rataRataSkorAll =
    rekapList.length > 0
      ? rekapList.reduce((sum, rekap) => sum + (rekap.rata_rata_skor || 0), 0) /
        rekapList.length
      : 0;

  // Define rows for DataTable
  const rows = rekapList.map((rekap, index) => ({
    id: rekap.id,
    no: index + 1,
    layanan: rekap.layanan || "-",
    nama_dinas: rekap.nama_dinas || "-",
    total_responden: rekap.total_responden || 0,
    rata_rata_nilai: rekap.rata_rata_nilai || 0,
    rata_rata_skor: rekap.rata_rata_skor || 0,
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          {/* Statistics Cards */}
          {!loading && selectedTahun && rekapList.length > 0 && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <MDBox p={2}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                    >
                      Total Layanan
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="bold">
                      {totalLayanan}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <MDBox p={2}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                    >
                      Total Responden
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="bold">
                      {totalResponden}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <MDBox p={2}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                    >
                      Rata-rata Nilai
                    </MDTypography>
                    <MDTypography
                      variant="h5"
                      fontWeight="bold"
                      color="success"
                    >
                      {rataRataNilaiAll > 0
                        ? rataRataNilaiAll.toFixed(2)
                        : "0.00"}
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <MDBox p={2}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="medium"
                    >
                      Rata-rata Skor
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="bold" color="info">
                      {rataRataSkorAll > 0
                        ? rataRataSkorAll.toFixed(2)
                        : "0.00"}
                    </MDTypography>
                    <MDProgress
                      value={rataRataSkorAll > 0 ? rataRataSkorAll : 0}
                      color="info"
                      variant="gradient"
                      label={false}
                    />
                  </MDBox>
                </Card>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Rekap Layanan Publik
                </MDTypography>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {/* Filter Section */}
                <Grid container spacing={3} mb={3}>
                  <Grid item xs={12} md={4}>
                    <MDBox
                      mb={1}
                      ml={0.5}
                      lineHeight={0}
                      display="inline-block"
                    >
                      <MDTypography
                        component="label"
                        variant="button"
                        fontWeight="regular"
                        color="text"
                      >
                        Pilih Tahun
                      </MDTypography>
                    </MDBox>
                    <Autocomplete
                      value={
                        tahunList.find((t) => t.toString() === selectedTahun) ||
                        null
                      }
                      onChange={(event, newValue) => {
                        handleTahunChange({
                          target: {
                            value: newValue ? newValue.toString() : "",
                          },
                        });
                      }}
                      options={tahunList}
                      getOptionLabel={(option) => option.toString() || ""}
                      disabled={loading}
                      renderInput={(params) => (
                        <MDInput {...params} variant="standard" />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Loading Indicator */}
                {loading && (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={4}
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data rekap...
                    </MDTypography>
                  </MDBox>
                )}

                {/* Data Table */}
                {!loading && selectedTahun && (
                  <>
                    {rekapList.length === 0 ? (
                      <MDBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={4}
                      >
                        <MDTypography variant="h6" color="text.secondary">
                          Tidak ada data rekap untuk tahun {selectedTahun}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <>
                        {isMobile ? (
                          <MDBox display="flex" flexDirection="column" gap={2}>
                            {rekapList.map((rekap, index) => (
                              <Card key={rekap.id}>
                                <MDBox p={2}>
                                  <MDTypography variant="h6" mb={2}>
                                    {index + 1}. {rekap.layanan || "-"}
                                  </MDTypography>
                                  <MDTypography
                                    variant="body2"
                                    color="text.secondary"
                                    mb={1}
                                  >
                                    Dinas: {rekap.nama_dinas || "-"}
                                  </MDTypography>
                                  <MDTypography
                                    variant="body2"
                                    color="text.secondary"
                                    mb={1}
                                  >
                                    Total Responden:{" "}
                                    {rekap.total_responden || 0}
                                  </MDTypography>
                                  <MDBox mb={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Rata-rata Nilai:{" "}
                                      {rekap.rata_rata_nilai > 0
                                        ? rekap.rata_rata_nilai.toFixed(2)
                                        : "0.00"}
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mb={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Rata-rata Skor:{" "}
                                      {rekap.rata_rata_skor > 0
                                        ? rekap.rata_rata_skor.toFixed(2)
                                        : "0.00"}
                                    </MDTypography>
                                    <MDProgress
                                      value={
                                        rekap.rata_rata_skor > 0
                                          ? rekap.rata_rata_skor
                                          : 0
                                      }
                                      color="info"
                                      variant="gradient"
                                      label={false}
                                    />
                                  </MDBox>
                                </MDBox>
                              </Card>
                            ))}
                          </MDBox>
                        ) : (
                          <DataTable
                            table={{ columns, rows }}
                            entriesPerPage={{
                              defaultValue: 10,
                              entries: [5, 10, 15, 20, 25],
                            }}
                            showTotalEntries={true}
                            canSearch={true}
                          />
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Empty State - No Selection */}
                {!selectedTahun && !loading && (
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    py={4}
                  >
                    <MDTypography variant="h6" color="text.secondary">
                      Pilih tahun untuk melihat data rekap layanan
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default RekapLayanan;
